type BasicUser = {
  email?: string | null;
  uid?: string | null;
  role?: string | null;
  emailVerified?: boolean | null;
};

export const DEFAULT_ADMIN_EMAILS = [
  'prateekdhuper@gmail.com',
  'prateek.dhuper@siemens.com',
  'jacky.jon12@gmail.com',
];

const parseList = (raw: string | undefined) =>
  (raw ?? '')
    .split(/[;,\s]+/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

export const isKnownAdminEmail = (email: string | null | undefined): boolean => {
  if (!email) {
    return false;
  }

  const cleanEmail = email.trim().toLowerCase();
  if (DEFAULT_ADMIN_EMAILS.includes(cleanEmail)) {
    return true;
  }

  const initialAdmin = (process.env.REACT_APP_INITIAL_ADMIN_EMAIL || '').trim().toLowerCase();
  if (initialAdmin && cleanEmail === initialAdmin) {
    return true;
  }

  const configuredEmails = parseList(process.env.REACT_APP_ADMIN_EMAILS);
  if (configuredEmails.includes(cleanEmail)) {
    return true;
  }

  return false;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');

    if (typeof globalThis.atob !== 'function') {
      return null;
    }

    const json = globalThis.atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const hasAdminClaim = (token: string | null | undefined): boolean => {
  if (!token) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return false;
  }

  if (payload.admin === true || payload.isAdmin === true) {
    return true;
  }

  if (typeof payload.role === 'string' && payload.role.toLowerCase() === 'admin') {
    return true;
  }

  if (Array.isArray(payload.roles)) {
    return payload.roles.some(
      (role) => typeof role === 'string' && role.toLowerCase() === 'admin'
    );
  }

  return false;
};

export const isAdminUser = (user: BasicUser | null | undefined, token?: string | null): boolean => {
  if (!user) {
    return false;
  }

  if (user.role === 'admin') {
    return true;
  }

  if (hasAdminClaim(token)) {
    return true;
  }

  const email = user.email?.trim().toLowerCase();
  if (email && isKnownAdminEmail(email) && user.emailVerified === true) {
    return true;
  }

  const adminUids = parseList(process.env.REACT_APP_ADMIN_UIDS);
  const uid = user.uid?.trim().toLowerCase();
  if (uid && adminUids.includes(uid)) {
    return true;
  }

  return false;
};
